import * as Sentry from '@sentry/react';
import { useEffect, useState } from 'react';
import Uppy from '@uppy/core';
import Dashboard from '@uppy/react/dashboard';
import AwsS3 from '@uppy/aws-s3';
import { AppError } from '@/lib/errors';
import '@uppy/core/css/style.css';
import '@uppy/dashboard/css/style.css';
import { getAuthHeaders } from '@/utils/auth';
import type { UppyFile, Meta, Body } from '@uppy/core';

type UploadErrorResponse = {
  status?: number;
  body?: unknown;
  bytesUploaded?: number;
  uploadURL?: string;
};

export default function MediaUploader({
  projectId,
  onUploadComplete,
}: {
  projectId: string;
  onUploadComplete: () => void;
}) {
  const [uppy] = useState(() => {
    return new Uppy().use(AwsS3, {
      shouldUseMultipart: true,
      async createMultipartUpload(file) {
        const response = await fetch('/api/client/storage/create-multipart', {
          method: 'POST',
          headers: await getAuthHeaders(),
          body: JSON.stringify({
            projectId,
            filename: file.name,
            contentType: file.type,
            fileSize: file.size,
          }),
        });
        const data = await response.json();
        if (!response.ok) {
          throw new AppError(
            data.message || 'Failed to create multipart upload',
            response.status,
            'FAILED_CREATE_MULTIPART_UPLOAD'
          );
        }
        return {
          uploadId: data.uploadId,
          key: data.key,
        };
      },
      async signPart(_file, { uploadId, key, partNumber, signal }) {
        if (!uploadId) {
          throw new AppError('Missing upload ID.', 400, 'MISSING_UPLOAD_ID');
        }
        const params = new URLSearchParams({
          uploadId,
          key,
          partNumber: String(partNumber),
        });
        const response = await fetch(
          `/api/client/storage/sign-part?${params}`,
          { signal, headers: await getAuthHeaders() }
        );
        const data = await response.json();
        if (!response.ok) {
          throw new AppError(
            data.message || 'Failed to sign part.',
            response.status,
            'FAILED_SIGN_MULTIPART'
          );
        }
        return { url: data.signedUrl };
      },
      async completeMultipartUpload(_file, { uploadId, key, parts }) {
        if (!uploadId) {
          throw new AppError('Missing upload ID.', 400, 'MISSING_UPLOAD_ID');
        }
        const response = await fetch('/api/client/storage/complete-multipart', {
          method: 'POST',
          headers: await getAuthHeaders(),
          body: JSON.stringify({ uploadId, key, parts }),
        });
        const data = await response.json();
        if (!response.ok) {
          throw new AppError(
            data.message || 'Failed to complete multipart.',
            response.status,
            'FAILED_COMPLETE_MULTIPART'
          );
        }
        return {};
      },
      async abortMultipartUpload(_file, { uploadId, key }) {
        if (!uploadId) {
          throw new AppError('Missing upload ID.', 400, 'MISSING_UPLOAD_ID');
        }
        const response = await fetch('/api/client/storage/abort-multipart', {
          method: 'POST',
          headers: await getAuthHeaders(),
          body: JSON.stringify({ uploadId, key }),
        });
        const data = await response.json();
        if (!response.ok) {
          throw new AppError(
            data.message || 'Failed to abort multipart.',
            response.status,
            'FAILED_ABORT_MULTIPART'
          );
        }
      },
      async listParts(_file, { uploadId, key }) {
        if (!uploadId) {
          throw new AppError('Missing upload ID.', 400, 'MISSING_UPLOAD_ID');
        }
        const params = new URLSearchParams({
          uploadId,
          key,
        });
        const response = await fetch(
          `/api/client/storage/list-parts?${params}`,
          { headers: await getAuthHeaders() }
        );
        const data = await response.json();
        if (!response.ok) {
          throw new AppError(
            data.message || 'Failed to list parts.',
            response.status,
            'FAILED_LIST_MULTIPART_PARTS'
          );
        }
        return data.parts;
      },
    });
  });

  useEffect(() => {
    uppy.on('complete', onUploadComplete);
    return () => {
      uppy.off('complete', onUploadComplete);
    };
  }, [uppy, onUploadComplete]);

  useEffect(() => {
    const onError = (error: Error) => {
      Sentry.captureException(error, { tags: { component: 'MediaUploader' } });
    };
    const onUploadError = (
      file: UppyFile<Meta, Body> | undefined,
      error: Error,
      response?: UploadErrorResponse
    ) => {
      Sentry.captureException(error, {
        tags: { component: 'MediaUploader', kind: 'upload-error' },
        extra: {
          fileName: file?.name,
          status: response?.status,
          body: response?.body,
        },
      });
    };

    uppy.on('error', onError);
    uppy.on('upload-error', onUploadError);

    return () => {
      uppy.off('error', onError);
      uppy.off('upload-error', onUploadError);
    };
  }, [uppy]);

  return (
    <Dashboard
      uppy={uppy}
      theme="dark"
      height={250}
      width="100%"
      hideUploadButton={false}
      proudlyDisplayPoweredByUppy={false}
      disableThumbnailGenerator={true}
    />
  );
}
